import { captureException } from '@sentry/nextjs'
import { EventPackage, Event, DeviceInfo, StatusMessage, ReferrerDeviceInfo, Page } from '@@/event_pb'
import { Action } from '@@/action_pb'
import { v4 as uuid } from 'uuid'
import { getItem, getUuid, isApp, isFromMobileFn, isServer, isSupportLocalStorage, removeItem } from '@/lib/utils'
import Router from 'next/router'
import { PageName } from '@@/page_pb'
import { BaseRouter, IEventParams } from '@/lib/interface/event'
import Cookies from 'js-cookie'
import axios, { isAxiosError } from 'axios'
import { PageNameEnum, KP_VAR } from './enums/pages'
import dsBridge from './dsbridge'
import { getAppVersionName } from './common-bridge'

const actionMap = Object.fromEntries(Object.entries(Action).map(([key, value]) => [value, key]))
const pageMap = Object.fromEntries(Object.entries(PageName).map(([key, value]) => [value, key]))

// need to set false in production mode
!isServer && process.env.NODE_ENV != 'production' && (window.__DEBUG_EVENT__ = true)

const LOCAL_STORAGE_KEY = 'cachedKPEvents'
const APPVERSION = 'kp_official'
const APPVERSIONCODE = '1.0.0'

declare global {
  interface Window {
    __DEBUG_EVENT__?: boolean
    dkBridge: any
  }
}

class DeviceInfoStore {
  private get domain() {
    return isServer ? '' : document.domain.match(/[^\.]*\.[^.]*$/)?.[0] ?? undefined
  }

  // KP_ADVERTISINGID / KP_DEVICEID
  public getApplicationId(type: KP_VAR) {
    if (!Cookies.get(type)) {
      Cookies.set(type, uuid(), {
        domain: this.domain,
        path: '/',
        expires: new Date(Date.now() + 3600 * 1000 * 24 * 365 * 20),
      })
    }
    return Cookies.get(type)
  }
}

class EventLogDebugger {
  get enabled() {
    return !isServer ? false : false
  }

  constructor(private eventLogger: EventLogProducer) {
    if (!isServer && process.env.NEXT_PUBLIC_ENV_NAME !== 'YES') {
      window.__DEBUG_EVENT__ = true
    }
  }
  log = (event: Event) => {
    if (this.enabled) return
    const eventPackage = this.eventLogger.eventPackage
    const device = eventPackage.getDevice()?.toObject()
    const eventAction = event.getAction()
    const action = actionMap[eventAction]
    const location = event.getLocation()
    const locationName = location ? pageMap[location.getPageName()] : null
    const locationParams = location ? JSON.stringify(Object.fromEntries(location.getParamsMap().toObject())) : null
    const target = event.getTarget()
    const targetName = target ? pageMap[target.getPageName()] : null
    const targetParams = target ? JSON.stringify(Object.fromEntries(target.getParamsMap().toObject())) : null
    const eventStatus = event.getStatus()
    const status = eventStatus ? JSON.stringify(eventStatus.toObject()) : null
    const extraMap = JSON.stringify(Object.fromEntries(event.getExtraMap().toObject()))
    const timestamp = event.getTimestamp()
    console.table({
      action,
      extraMap,
      status,
      locationName,
      locationParams,
      targetName,
      targetParams,
      timestamp,
      ...device,
    })
  }
}

class EventLogProducer {
  public eventPackage = new EventPackage()
  private debugger = new EventLogDebugger(this)

  public init() {
    if (isServer) return
    this.getCachedEvents()
    this.pushEnterWebsite()
    eventLogConsumer.init()
  }
  private pushEnterWebsite() {
    eventLogProducer.push({
      action: Action.ENTERKPWEBSITE,
    })
  }

  private getCachedEvents() {
    try {
      const cachedNewEvents = isSupportLocalStorage() ? localStorage?.getItem?.(LOCAL_STORAGE_KEY) : null
      if (cachedNewEvents) {
        const unit8Array = new Uint8Array(
          atob(cachedNewEvents)
            .split('')
            .map((s) => s.charCodeAt(0)),
        )
        this.eventPackage = EventPackage.deserializeBinary(unit8Array)
      }
    } catch (error) {
      captureException(error)
      this.clearEvents()
    }
    const deviceInfo = new DeviceInfo()
    deviceInfo.setAdvertisingId(
      window.dkBridge
        ? window.dkBridge.getAdvertisingId?.()
        : dsBridge.call('getAdvertisingId', {}) || 'ADVERTISING_ID',
    )
    const ua = navigator.userAgent.toLocaleLowerCase()
    if (ua.includes('iphone')) {
      deviceInfo.setOsType(DeviceInfo.OSType.IOS)
    } else if (ua.includes('android')) {
      deviceInfo.setOsType(DeviceInfo.OSType.ANDROID)
    } else {
      deviceInfo.setOsType(DeviceInfo.OSType.OTHERS)
    }
    if (isFromMobileFn(ua)) {
      deviceInfo.setDeviceType(3)
    } else {
      deviceInfo.setDeviceType(1)
    }
    deviceInfo.setAppVersion(isApp() ? `${getAppVersionName(APPVERSIONCODE)};${APPVERSIONCODE}` : APPVERSIONCODE)
    deviceInfo.setAppVersionCode(
      window.dkBridge ? window.dkBridge.getAppVersionCode?.() : dsBridge.call('getAppVersionCode', {}) || '9999',
    )
    deviceInfo.setReferrerInfo(this.getReferrerExtraInfo())
    deviceInfo.setUuid(
      window.dkBridge ? window.dkBridge.getDeviceUUID?.() : dsBridge.call('getDeviceUUID', {}) || getUuid(),
    )
    deviceInfo.setSource('')
    const userInfo = getItem('userInfo')
    deviceInfo.setUserId(
      window.dkBridge ? window.dkBridge.getUserId?.() : dsBridge.call('getUserId', {}) || userInfo?.userId || '',
    )
    deviceInfo.setUserType(userInfo?.userType || userInfo?.type || 0)
    this.eventPackage.setDevice(deviceInfo)
  }

  private getReferrerExtraInfo() {
    const deviceInfo = new ReferrerDeviceInfo()
    const extra = {
      original_URL: window.location.href,
      referrer_URL: document.referrer,
    }
    Object.entries(extra).forEach(([key, value]) => deviceInfo.getExtraMap().set(key, value))
    return deviceInfo
  }

  public clearEvents() {
    if (isServer) return
    this.eventPackage.clearEventsList()
    removeItem?.(LOCAL_STORAGE_KEY)
  }

  public push(eventParams: IEventParams) {
    if (isServer) return
    const { action, statusMessageInfo, extra, target, targetParams, location, locationParams } = eventParams
    if (!action) return

    const event = new Event()
    event.setTimestamp(Date.now())
    event.setAction(action)

    // Location
    const locationPage = this.parseNextRouteToPage(location || Router)
    if (!!locationPage) {
      event.setLocation(locationPage)
    } else {
      event.clearLocation()
    }

    // Target
    const targetPage = this.parseNextRouteToPage(target)
    if (!!targetPage) {
      event.setTarget(targetPage)
    } else {
      event.clearTarget()
    }

    // Status
    if (statusMessageInfo) {
      const message = new StatusMessage()
      message.setStatusCode(statusMessageInfo.statusCode)
      message.setStatus(statusMessageInfo.status)
      message.setMessage(statusMessageInfo.message || '')
      event.setStatus(message)
    } else {
      event.clearStatus()
    }

    // extra
    if (extra) {
      Object.entries(extra).forEach(([key, value]) => event.getExtraMap().set(key, value))
    } else {
      event.clearExtraMap()
    }
    this.eventPackage.getEventsList().push(event)
    const cachedNewEvents = window.btoa(String.fromCharCode(...this.eventPackage.serializeBinary()))
    if (isSupportLocalStorage()) {
      localStorage?.setItem?.(LOCAL_STORAGE_KEY, cachedNewEvents)
    } else {
      this.clearEvents()
    }
  }

  private parseRouteParam = (dictionary: any) => {
    if (!isServer) {
      return Object.entries(dictionary || {}).reduce<{ [x: string]: string }>((acc, [key, value]) => {
        return Object.assign({}, acc, { [`${key}`]: value })
      }, {})
    }
  }

  public parseNextRouteToPage = (router: BaseRouter | undefined) => {
    if (!router) return undefined
    const originRoute = router.route
    const route = originRoute === '' ? '/' : originRoute
    if (PageNameEnum[route] !== undefined) {
      const page = new Page()
      const pageQuery = this.parseRouteParam(router.query) || {}
      Object.entries(pageQuery).forEach(([key, value]) => page.getParamsMap().set(key, value || null))
      page.setPageName(PageNameEnum[route])
      return page
    } else if (originRoute === '/education/page/[page]') {
      const page = new Page()
      const pageQuery = this.parseRouteParam(router.query) || {}
      Object.entries(pageQuery).forEach(([key, value]) => page.getParamsMap().set(key, value || null))
      page.setPageName(256)
      return page
    } else {
      return undefined
    }
  }
}

class EventLogConsumer {
  private abortController: AbortController | null = null
  private timer: number | null = null

  public init() {
    if (isServer) return false
    if (!!eventLogProducer.eventPackage.getEventsList().length) {
      this.flushEvents()
    } else {
      this.initTimer()
    }
  }

  private async flushEvents() {
    if (process.env.NEXT_PUBLIC_ENV_NAME === 'NO' || location.href.includes('localhost')) return
    if (isServer) return false
    this.abortController?.abort()
    this.abortController = new AbortController()
    const eventsList = eventLogProducer.eventPackage.getEventsList()
    const eventsCount = eventsList.length

    if (eventsCount > 0) {
      // Capture how many events to send BEFORE any async operations
      // This prevents clearing events that were added during the flush
      const eventsToSendCount = eventsCount

      eventLogProducer.eventPackage.setTimestamp(Date.now())
      try {
        const data = window.btoa(String.fromCharCode(...eventLogProducer.eventPackage.serializeBinary()))
        await axios.request({
          method: 'POST',
          url: '/collect',
          headers: {
            'Content-Type': 'text/plain',
          },
          data,
          timeout: 60000,
          signal: this.abortController.signal,
        })

        // Only remove the events that were successfully sent (first N events)
        // Get remaining events that were added after flush started
        const remainingEvents = eventsList.slice(eventsToSendCount)

        // Clear all and re-add only the remaining events
        eventLogProducer.eventPackage.clearEventsList()
        remainingEvents.forEach((event) => {
          eventLogProducer.eventPackage.getEventsList().push(event)
        })

        // Update cache
        const cachedNewEvents = window.btoa(String.fromCharCode(...eventLogProducer.eventPackage.serializeBinary()))
        if (isSupportLocalStorage()) {
          localStorage?.setItem?.(LOCAL_STORAGE_KEY, cachedNewEvents)
        }
      } catch (error) {
        if (isAxiosError(error) && error.code === 'ERR_CANCELED') {
          return
        }
        captureException(error)
        this.abortController = null
      }
    }
    if (this.timer) clearTimeout(this.timer)
    this.initTimer()
  }

  private initTimer() {
    if (isServer) return false
    this.timer = window.setTimeout(() => {
      this.flushEvents()
    }, 2000)
  }
}

export const deviceInfoStore = new DeviceInfoStore()
export const eventLogProducer = new EventLogProducer()
export const eventLogConsumer = new EventLogConsumer()
